import { Users, Crown, ShieldCheck, Sigma, CheckCircle2, UserCheck, Percent } from "lucide-react";
import { TRY, fmtDateTime } from "@/lib/format";

const roleLabel = (r) => (r === "admin" ? "Yönetici" : r === "worker" ? "Depo Müdürü" : "—");

// İskonto geçmişini sadeleştir: aynı kişi+ürün için yalnız SON değer kalır (her tuş vuruşunda eklenen ara değerler gizlenir)
const collapseDisc = (arr) => {
  const out = [], idx = new Map();
  for (const d of arr || []) {
    if (d.type === "override") { out.push(d); continue; }
    const key = `${d.id || d.name}|${d.product_id || d.product_name || ""}`;
    if (idx.has(key)) out[idx.get(key)] = d;
    else { idx.set(key, out.length); out.push(d); }
  }
  return out;
};

// Sepet Katılım Geçmişi (yalnız yönetici): sepeti açan, katılan yöneticiler, iskontoyu uygulayanlar, tamamlayan.
export function ParticipationInfo({ sale, testid }) {
  const joins = sale?.admin_joins || [];
  const parts = sale?.participants || [];
  const disc = collapseDisc(sale?.discount_log || []);
  const hasAny = sale?.cart_id || joins.length || parts.length || disc.length;

  if (!hasAny) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-muted-foreground" data-testid={testid || "qs-participation-empty"}>
        Bu satış için sepet katılım kaydı yok (doğrudan satış).
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5" data-testid={testid || "qs-participation"}>
      <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-indigo-700">
        <Users className="h-3.5 w-3.5" />Sepet Katılım Geçmişi
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="rounded-lg bg-white border border-indigo-100 px-2.5 py-1.5" data-testid="qs-part-owner">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Crown className="h-3 w-3 text-amber-500" />Sepeti Açan</div>
          <div className="text-sm font-semibold text-slate-800 truncate">{sale.cart_owner_name || "—"}</div>
        </div>
        <div className="rounded-lg bg-white border border-indigo-100 px-2.5 py-1.5" data-testid="qs-part-completer">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" />Satışı Tamamlayan</div>
          <div className="text-sm font-semibold text-slate-800 truncate">{sale.completed_by || sale.user_name || "—"}</div>
        </div>
      </div>

      {joins.length > 0 && (
        <div data-testid="qs-part-joins">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-indigo-600" />Sepete Katılan Yöneticiler</div>
          <div className="space-y-1">
            {joins.map((j, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-white border border-indigo-100 px-2.5 py-1.5" data-testid={`qs-part-join-${i}`}>
                <span className="text-sm font-semibold text-slate-800 truncate">{j.name}</span>
                <span className="text-[11px] tnum text-muted-foreground whitespace-nowrap">{fmtDateTime(j.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {disc.length > 0 && (
        <div data-testid="qs-part-discounts">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Sigma className="h-3 w-3 text-orange-600" />İskontoyu Uygulayan</div>
          <div className="space-y-1">
            {disc.map((d, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-lg bg-white border border-orange-100 px-2.5 py-1.5" data-testid={`qs-part-disc-${i}`}>
                <span className="text-sm font-semibold text-slate-800 flex flex-wrap items-center gap-1.5 min-w-0">
                  <span className="break-words">{d.name}</span>
                  {d.type === "override"
                    ? <span className="rounded-full bg-orange-100 text-orange-700 text-[9px] font-bold px-1.5 py-0.5 whitespace-nowrap">Pazarlık −{TRY(d.amount)}</span>
                    : <span className="rounded-full bg-orange-50 text-orange-700 text-[9px] font-bold px-1.5 py-0.5 inline-flex items-center gap-0.5 whitespace-nowrap"><Percent className="h-2.5 w-2.5" />{d.product_name ? `${d.product_name} · %${d.sale_discount}` : `%${d.sale_discount}`}</span>}
                </span>
                <span className="text-[11px] tnum text-muted-foreground whitespace-nowrap">{fmtDateTime(d.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {parts.length > 0 && (
        <div data-testid="qs-part-all">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><UserCheck className="h-3 w-3 text-slate-500" />Tüm Katılımcılar</div>
          <div className="flex flex-wrap gap-1.5">
            {parts.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px]" data-testid={`qs-part-user-${i}`} title={`${fmtDateTime(p.first_at)} → ${fmtDateTime(p.last_at)} · ${p.ops || 0} işlem`}>
                <span className="font-semibold text-slate-700">{p.name}</span>
                <span className="text-muted-foreground">· {roleLabel(p.role)}</span>
                {p.is_owner && <Crown className="h-2.5 w-2.5 text-amber-500" />}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
